import { clamp } from "./math.js";

export function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function detectPerformanceTier() {
  if (prefersReducedMotion()) {
    return "reduced";
  }

  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const cores = navigator.hardwareConcurrency ?? 4;
  const memory = navigator.deviceMemory ?? 4;
  const pixels = window.innerWidth * window.innerHeight * window.devicePixelRatio;

  if (coarse || cores <= 4 || memory <= 4 || pixels > 9_000_000) {
    return "low";
  }
  if (cores >= 8 && memory >= 8 && pixels < 7_500_000) {
    return "high";
  }
  return "medium";
}

export class PerformanceMonitor {
  #store;
  #config;
  #frames = [];
  #lastPublishedAt = 0;

  constructor(store, config) {
    this.#store = store;
    this.#config = config;
  }

  sample(timestamp) {
    this.#frames.push(timestamp);
    const cutoff = timestamp - this.#config.sampleWindowMs;
    while (this.#frames[0] < cutoff) {
      this.#frames.shift();
    }

    if (
      timestamp - this.#lastPublishedAt < 1000 ||
      this.#frames.length < 2
    ) {
      return;
    }

    this.#lastPublishedAt = timestamp;
    const elapsed =
      (this.#frames.at(-1) - this.#frames[0]) / 1000 || 1;
    const fps = clamp(
      0,
      Math.round((this.#frames.length - 1) / elapsed),
      120
    );
    const currentTier = this.#store.get().performance.tier;
    let nextTier = currentTier;

    if (
      currentTier === "high" &&
      this.#frames[0] <= cutoff + 250 &&
      fps < this.#config.highMinimumFps
    ) {
      nextTier = "medium";
    } else if (
      currentTier === "medium" &&
      this.#frames[0] <= cutoff + 250 &&
      fps < this.#config.mediumMinimumFps
    ) {
      nextTier = "low";
    }

    this.#store.patch(
      { performance: { fps, tier: nextTier } },
      nextTier !== currentTier
        ? "performance:downgrade"
        : "performance:sample"
    );
  }
}
